import { inject, observer } from 'mobx-react';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import {
  useEffect,
  useState,
} from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { connect } from 'react-redux';

import { Link } from 'react-router-dom';
import { dateTimeStringToUnix, unixToDateTimeString } from 'modules/core/utils/date';
import * as actions from './store/actions';
import { DataTablePage } from '../core/containers';
import { Button } from '../core/components/buttons';
import {
  clientActions,
  ClientStore,
} from './store/clientStore';
import { QuickSearch } from '../core/components/search';
import * as URL from '../../lib/router/url';
import { ClientProfile } from './api/clientApi';
import BonusEdit from './BonusEdit';
import sumBy from 'lodash/sumBy';

const styles = {
  bonusHeader: {
    fontSize: '1.5em',
    marginRight: '24px',
  },
};

interface BonusHistoryProps {
  clientStore?: ClientStore,
  clientId?: string,
  clientProfile?: ClientProfile,
  getClient?: Function,
}

const columns = [
  {
    title: 'Заявка',
    name: 'number',
    width: 160,
    getCellValue: (row) => <Link to={ `${ URL.URL_ORDERS_VIEW_ONE }/${ row.orderId }` }>{ row.orderNumber }</Link>,
  },
  { title: 'Счёт', name: 'invoiceNumber', width: 150 },
  {
    title: 'Дата и время',
    name: 'createdDate',
    width: 180,
    getCellValue: (row) => {
      const operationDate = row?.paymentDate ? row.paymentDate : row?.createdDate;
      return operationDate ? unixToDateTimeString(operationDate, 'DD.MM.YYYY HH:mm') : '';
    },
    sortValueConverter: (value) => (value ? dateTimeStringToUnix(value) : ''),
  },
  { title: 'Начислено', name: 'bonusReceived', width: 120 },
  { title: 'Списано', name: 'bonusSpent', width: 120 },
  { title: 'Партнёр', name: 'partnerName', width: 160 },
  { title: 'Юр.лицо', name: 'partnerLegalName', width: 160 },
  { title: 'Сервис', name: 'serviceName', width: 160 },
  { title: 'Тип начисление', name: 'operationTypeRefValueName', width: 160 },
  { title: 'Начислил', name: 'createdByUserName', width: 180 },
  { title: 'Комментарий', name: 'comment', width: 600 },
];

const BonusHistory = (props: BonusHistoryProps) => {
  const {
    clientStore,
    clientId,
    clientProfile,
    getClient,
  } = props;

  const { bonusHistory, bonusHistoryByClientId } = clientStore;

  const [loading, setLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [showBonusEdit, setShowBonusEdit] = useState<boolean>(false);

  const loadData = async (anyway = false) => {
    setLoading(true);
    try {
      if (anyway || (clientId !== bonusHistoryByClientId)) {
        await clientActions.getBonusHistory(clientId);
      }
      if (anyway || (clientId !== clientProfile?.id)) {
        await getClient(clientId);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangeQuickSearch = (event) => {
    const { value } = event.target || {};

    setSearchValue((value?.length > 0) ? value : '');
  };

  const handleSubmitQuickSearch = (value) => {
    handleChangeQuickSearch({ target: { value } });
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const sumBonusReceived = sumBy(bonusHistory,'bonusReceived');
  const sumBonusSpent = sumBy(bonusHistory,'bonusSpent');

  return (
    <Col md={ 12 }>
      <Row>
        <Col>
          <DataTablePage
            loading={ loading }
            header='История бонусов(оплаченных счетов)'
            columns={ columns }
            data={ bonusHistory }
            dataLength={ bonusHistory?.length }
            error={ clientStore.error }
          >
            { ({ components }) => (
              <>
                <Row>
                  <Col md={ 7 }>
                    <components.PageHeader />
                  </Col>

                  <Col md={ 5 } xs={ 12 }>
                    <QuickSearch
                      name='searchText'
                      value={ searchValue }
                      placeholder='Быстрый поиск'
                      disabled={ loading }
                      onClear={ handleSubmitQuickSearch }
                      onChange={ handleChangeQuickSearch }
                      onSubmit={ handleSubmitQuickSearch }
                    />
                  </Col>
                  <Col>
                    <span style={ styles.bonusHeader }>
                    Бонусы клиента: { clientProfile?.bonus ?? 0 }
                    </span>
                    <span style={ styles.bonusHeader }>
                    Начислено: { sumBonusReceived ?? 0 }
                    </span>
                    <span style={ styles.bonusHeader }>
                    Списано: { sumBonusSpent }
                    </span>
                  </Col>
                </Row>

                <Row>
                  <Col md={ 12 }>
                    <ButtonToolbar className='page-buttons float-end align-items-start'>
                      <Button
                        variant='primary'
                        text='Корректировка бонусов'
                        icon='diamond'
                        // disabled={ loading || readOnly || !permissions?.permissionInsert }
                        onClick={ () => { setShowBonusEdit(true); } }
                      />
                    </ButtonToolbar>
                  </Col>
                </Row>

                <Row>
                  <Col lg={ 12 }>
                    <components.DataTable
                      // columnsResizingMode='nextColumn'
                      searchValue={ searchValue || '' }
                      errorRetryHandler={ loadData }
                      hideToolbar
                      localSorting
                      localPaging
                    />
                  </Col>
                </Row>
                { showBonusEdit && (
                  <BonusEdit
                    clientId={ clientId }
                    onClose={ () => {
                      setShowBonusEdit(false);
                    } }
                    onSubmit={ () => {
                      setShowBonusEdit(false);
                      loadData(true);
                    } }
                  />
                ) }
              </>
            ) }
          </DataTablePage>
        </Col>
      </Row>
    </Col>
  );
};

const mapStateToProps = (state) => ({
  clientProfile: state.clients?.item,
});

const mapDispatchToProps = {
  getClient: actions.getClientInfo,
};

export default connect(mapStateToProps, mapDispatchToProps)(inject('clientStore')(observer(BonusHistory)));
